import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { CoinClient } from '../../../core/openapi/bff-admin-api';
import { SpecialIcon, ToastService } from '@ui-lib/components';
import { exhaustMap, map, Observable, of, startWith, Subject, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { createCoinPlaceholderSvg, toIconKey } from '../../helpers/icon-helpers';

@Injectable()
export class CoinDictionaryService {
  private readonly coinClient = inject(CoinClient);
  private readonly toastService = inject(ToastService);

  public readonly totalCount: WritableSignal<number> = signal<number>(0);
  public readonly isLoading: WritableSignal<boolean> = signal<boolean>(false);
  public readonly hasLoaded: WritableSignal<boolean> = signal<boolean>(false);

  private readonly origin$: Subject<void> = new Subject<void>();

  public readonly source$ = this.origin$.pipe(
    startWith(void 0),
    tap((): void => {
      this.isLoading.set(true);
      this.hasLoaded.set(false);
    }),
    exhaustMap(() =>
      this.coinClient.v1CoinGet().pipe(
        tap((items): void => {
          this.totalCount.set(items?.length ?? 0);
        }),
        map((items): IDictionaryItem<number>[] =>
          Array.isArray(items)
            ? items
                .filter((item): boolean => item.id !== undefined && item.id !== null)
                .map((item): IDictionaryItem<number> => {
                  const specialIcon: SpecialIcon | null = toIconKey(item.ticker);
                  return {
                    id: item.id,
                    name: item.ticker ?? item.name ?? '-',
                    icon: specialIcon ?? createCoinPlaceholderSvg(item.ticker),
                    iconType: specialIcon ? 'special' : 'data/image',
                    iconPosition: 'left',
                  };
                })
            : [],
        ),
        catchError((err: unknown): Observable<IDictionaryItem<number>[]> => {
          this.toastService.error({
            message: 'monitoring.selector.toast.coins-fetching-error',
          });

          console.error('Error fetching coin dictionary items:', err);

          return of([]);
        }),
      ),
    ),
    tap((): void => {
      this.isLoading.set(false);
      this.hasLoaded.set(true);
    }),
  );
}
